import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, Trash2, ShieldCheck, Clock, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

const TABS = [
  { key: "pending", label: "En attente", Icon: Clock },
  { key: "published", label: "Publiés", Icon: CheckCircle2 },
  { key: "rejected", label: "Refusés", Icon: XCircle },
];

export default function AdminTutorials() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = () => {
    setLoading(true);
    api
      .get("/admin/tutorials", { params: { status: tab, limit: 50 } })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const onApprove = async (id) => {
    try {
      await api.patch(`/admin/tutorials/${id}/approve`);
      toast.success("Tuto publié ✓");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const onReject = async (id) => {
    if (rejectReason.trim().length < 3) {
      toast.error("Merci d'expliquer le motif du refus");
      return;
    }
    try {
      await api.patch(`/admin/tutorials/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Tuto refusé — l'auteur sera notifié");
      setRejectReason("");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement ce tuto ?")) return;
    try {
      await api.delete(`/admin/tutorials/${id}`);
      toast.success("Tuto supprimé");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (detail) {
    return (
      <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10" data-testid="admin-tuto-detail">
        <button
          onClick={() => { setDetail(null); setRejectReason(""); }}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          data-testid="admin-tuto-back"
        >
          <ArrowLeft className="h-4 w-4" /> Retour à la liste
        </button>

        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          <Badge className="rounded-full capitalize">{detail.category.replace("-", " ")}</Badge>
          {detail.spa_brand && <Badge variant="outline" className="rounded-full">{detail.spa_brand}</Badge>}
          {(detail.tags || []).map((t) => (
            <Badge key={t} variant="outline" className="rounded-full text-[10px]">#{t}</Badge>
          ))}
        </div>

        <h1 className="font-display text-2xl lg:text-3xl font-black tracking-tight">{detail.title}</h1>
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <span>Par <strong className="text-foreground">{detail.author_name}</strong></span>
          {detail.author_is_technician && (
            <Badge className="rounded-full bg-secondary/10 text-secondary border-0 gap-1">
              <ShieldCheck className="h-3 w-3" /> Technicien vérifié
            </Badge>
          )}
        </div>

        {detail.summary && (
          <p className="mt-4 text-sm italic text-muted-foreground border-l-4 border-primary/40 pl-4">
            {detail.summary}
          </p>
        )}

        {detail.cover_photo_url && (
          <img src={`${backend}${detail.cover_photo_url}`} alt="" className="mt-6 w-full aspect-video object-cover rounded-xl" />
        )}
        {detail.video_url && (
          <div className="mt-4 text-xs text-primary break-all">
            🎥 Vidéo : <a href={detail.video_url} target="_blank" rel="noopener noreferrer" className="underline">{detail.video_url}</a>
          </div>
        )}

        <div className="prose prose-slate max-w-none mt-8 prose-headings:font-display">
          <ReactMarkdown>{detail.body_markdown}</ReactMarkdown>
        </div>

        {(detail.photos || []).length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-2">
            {detail.photos.map((p, i) => (
              <a key={p.path} href={`${backend}${p.url}`} target="_blank" rel="noopener noreferrer">
                <img src={`${backend}${p.url}`} alt={p.filename} className="aspect-square object-cover rounded-md" />
              </a>
            ))}
          </div>
        )}

        <Card className="mt-10 p-6 bg-slate-50 border-2">
          <h3 className="font-display font-bold text-lg">Actions de modération</h3>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => onApprove(detail.id)}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-11 px-6"
              data-testid="admin-tuto-approve"
            >
              <Check className="h-4 w-4 mr-2" /> Approuver et publier
            </Button>
            <Button
              onClick={() => onDelete(detail.id)}
              variant="outline"
              className="rounded-full h-11 px-6 text-red-600 hover:text-red-700"
              data-testid="admin-tuto-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Supprimer
            </Button>
          </div>
          <div className="mt-6 space-y-2">
            <label className="text-sm font-semibold">Refuser avec motif :</label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex. Contenu peu détaillé, photos manquantes, non-conforme à la charte…"
              rows={3}
              maxLength={500}
              data-testid="admin-tuto-reject-reason"
            />
            <Button
              onClick={() => onReject(detail.id)}
              variant="outline"
              className="rounded-full h-10 px-5 text-red-600 hover:text-red-700"
              data-testid="admin-tuto-reject"
            >
              <X className="h-4 w-4 mr-2" /> Refuser
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10" data-testid="admin-tutorials-page">
      <div className="eyebrow text-secondary">Admin</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
        Modération des tutos
      </h1>

      <div className="mt-8 flex gap-2 border-b" data-testid="admin-tuto-tabs">
        {TABS.map((t) => {
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`admin-tuto-tab-${t.key}`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="mt-10 text-sm text-muted-foreground">Aucun tuto dans cet onglet.</p>
      ) : (
        <div className="mt-6 space-y-3">
          <p className="text-xs text-muted-foreground">{total} tuto{total > 1 ? "s" : ""}</p>
          {items.map((t) => (
            <Card
              key={t.id}
              className="p-4 hover:border-primary/40 cursor-pointer"
              onClick={() => setDetail(t)}
              data-testid={`admin-tuto-item-${t.slug}`}
            >
              <div className="flex gap-4">
                <div className="w-24 shrink-0 aspect-video rounded bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 overflow-hidden">
                  {t.cover_photo_url && (
                    <img src={`${backend}${t.cover_photo_url}`} alt="" className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="rounded-full capitalize">{t.category.replace("-", " ")}</Badge>
                    {t.spa_brand && <Badge variant="outline" className="rounded-full">{t.spa_brand}</Badge>}
                    {t.author_is_technician && (
                      <Badge className="rounded-full bg-secondary/10 text-secondary border-0 gap-1">
                        <ShieldCheck className="h-3 w-3" /> Tech
                      </Badge>
                    )}
                  </div>
                  <h3 className="mt-1 font-bold truncate">{t.title}</h3>
                  <p className="text-xs text-muted-foreground line-clamp-1">{t.summary}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Par {t.author_name} · {new Date(t.created_at).toLocaleDateString("fr-FR")}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
